import { useCallback, useEffect, useRef, useState } from 'react';

export type IntegrityCamera = {
  stream: MediaStream | null;
  granted: boolean;
  // Re-runs getUserMedia for the intro page's "allow camera access" link.
  // After a hard deny the browser won't re-prompt — the promise just rejects
  // again, so the link stays visible for the candidate to fix it in browser
  // settings and retry.
  request: () => void;
};

// Acquires and owns the camera stream for online-assessment integrity
// monitoring: prompts on mount while enabled, drops back to the ungranted
// state when the track ends (permission revoked, device unplugged), and stops
// the tracks on disable/unmount.
export function useIntegrityCamera(enabled: boolean): IntegrityCamera {
  const [stream, setStream] = useState<MediaStream | null>(null);
  const activeStreamRef = useRef<MediaStream | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const request = useCallback(async () => {
    if (!enabledRef.current || activeStreamRef.current) return;
    try {
      const next = await navigator.mediaDevices.getUserMedia({ video: true });
      if (!enabledRef.current) {
        next.getTracks().forEach((track) => track.stop());
        return;
      }
      activeStreamRef.current = next;
      // Revoking permission or unplugging the device ends the track without
      // any other signal.
      next.getVideoTracks().forEach((track) => {
        track.onended = () => {
          if (activeStreamRef.current === next) activeStreamRef.current = null;
          setStream((current) => (current === next ? null : current));
        };
      });
      setStream(next);
    } catch {
      // Denied or no camera — the caller keeps showing its retry link.
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void request();
    return () => {
      activeStreamRef.current?.getTracks().forEach((track) => track.stop());
      activeStreamRef.current = null;
      setStream(null);
    };
  }, [enabled, request]);

  return { stream, granted: !!stream, request };
}
